import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Quote } from "lucide-react";
import coverTreinarNovos from "@/assets/cover-treinar-vendedores-novos.jpg";

const ComoTreinarVendedoresNovos = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Como Treinar Vendedores Novos: O Que Realmente Encurta o Ramp-up",
    description:
      "Como treinar vendedores novos sem manual engessado: onboarding vendedor conduzido por conversa, treinamento da equipe comercial na prática e ramp-up de vendas mais curto.",
    image: "https://bianchimbusiness.com.br/cover-treinar-vendedores-novos.jpg",
    datePublished: "2026-08-28",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/como-treinar-vendedores-novos",
    },
  };

  return (
    <>
      <SEOHead
        title="Como Treinar Vendedores Novos: O Que Encurta o Ramp-up de Vendas"
        description="Como treinar vendedores novos de verdade: onboarding vendedor por conversa e simulação, treinamento da equipe comercial no dia a dia e ramp-up de vendas mais curto."
        keywords="como treinar vendedores novos, onboarding vendedor, treinamento equipe comercial, ramp-up vendas, rampagem de vendedores, treinar vendedor iniciante, integração de vendedores, desenvolvimento de time comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/como-treinar-vendedores-novos"
        ogType="article"
        articlePublishedTime="2026-08-28"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverTreinarNovos}
                  alt="Gestor comercial conversando com um vendedor novo durante o onboarding"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Desenvolvimento de Time
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Como treinar vendedores novos:{" "}
                    <span className="text-gradient-brand">o que realmente encurta o ramp-up</span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>11 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>28 Ago 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Corpo */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto text-muted-foreground text-lg leading-relaxed space-y-6">
                  <p className="text-xl">
                    <strong className="text-foreground">
                      Vendedor novo não erra por falta de material. Erra por falta de repetição
                      acompanhada.
                    </strong>{" "}
                    Ele recebe a apresentação institucional, o acesso ao CRM, a tabela de preços e um
                    "qualquer dúvida me chama". Duas semanas depois está no telefone tentando
                    reconstruir, de memória, uma conversa que nunca praticou em voz alta. O resultado
                    aparece no funil: muita atividade, pouca reunião, e um discurso que muda de forma
                    a cada ligação.
                  </p>
                  <p>
                    Quando falamos de <strong className="text-foreground">onboarding vendedor</strong>,
                    a discussão costuma girar em torno de conteúdo — quantos vídeos, quantas
                    apostilas, quantas horas de treinamento sobre o produto. Mas o gargalo raramente
                    é informação. É a distância entre saber explicar o produto e conseguir conduzir
                    uma conversa comercial sob pressão, com um cliente impaciente do outro lado,
                    dizendo que já tem fornecedor e que está sem tempo.
                  </p>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    O erro clássico: ensinar produto antes de ensinar conversa
                  </h2>
                  <p>
                    A maioria das empresas começa pelo catálogo. Faz sentido intuitivo: como vender
                    algo que não se conhece? Só que a consequência é previsível. O vendedor sai da
                    primeira semana virando um excelente explicador técnico e um péssimo condutor de
                    diálogo. Ele fala sobre o que a empresa faz, não sobre o problema que o cliente
                    tem. Ouve pouco, apresenta cedo e entrega preço antes de construir valor.
                  </p>
                  <p>
                    Vi isso acontecer em uma indústria do interior de São Paulo. O time comercial
                    tinha um treinamento de produto impecável, com apostila, prova e certificado. Nos
                    áudios das ligações, o padrão era o mesmo: o vendedor levava quatro minutos
                    descrevendo processo produtivo e certificações antes de saber quem era o
                    interlocutor, o que ele comprava hoje e por que trocaria. O cliente escutava,
                    agradecia e pedia proposta por e-mail. A proposta ia, e sumia. Não era falta de
                    conhecimento — era falta de condução.
                  </p>
                  <p>
                    A inversão que funciona é simples e desconfortável: nos primeiros dias, o vendedor
                    novo aprende a conversar. Aprende a abrir uma ligação em quinze segundos sem soar
                    telemarketing, a fazer três perguntas que revelam contexto real, a repetir com as
                    próprias palavras o que ouviu e a pedir um próximo passo com data. Produto entra
                    logo depois, mas entra encaixado dentro dessa conversa, e não como monólogo.
                  </p>

                  <div className="border-l-4 border-primary pl-5 py-2 my-8">
                    <Quote className="w-6 h-6 text-primary mb-2" />
                    <p className="text-foreground text-xl font-medium italic">
                      "Vendedor novo não precisa saber tudo sobre o produto. Precisa saber conduzir
                      uma conversa em que o cliente fale mais do que ele."
                    </p>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    Ramp-up de vendas é uma curva de exposição, não um calendário
                  </h2>
                  <p>
                    Muita gente trata <strong className="text-foreground">ramp-up de vendas</strong>{" "}
                    como prazo: tantos dias para o vendedor bater meta. O prazo é consequência. O que
                    determina a velocidade é a quantidade de conversas reais que ele viveu, com
                    correção imediata depois de cada uma. Um vendedor que fez quarenta simulações e
                    ouviu feedback em todas chega ao mercado mais pronto do que outro que passou o
                    mesmo período lendo material e assistindo call de colega.
                  </p>
                  <p>
                    Pense em dois cenários concretos. No primeiro, o gestor libera o vendedor para
                    ligar sozinho já na segunda semana, "para pegar prática". Ele queima cinquenta
                    leads aprendendo o que deveria ter aprendido em ambiente controlado, e cria vícios
                    difíceis de desmontar depois. No segundo, o vendedor passa esse mesmo tempo
                    conduzindo conversas simuladas contra personas que reagem como cliente de
                    verdade: o comprador apressado, o técnico cético, o dono que só fala de preço.
                    Quando ele chega ao telefone, já ouviu "está caro" trinta vezes e não congela.
                  </p>
                  <p>
                    A diferença não está no volume de horas, está na densidade de repetição com
                    correção. É o mesmo princípio de qualquer habilidade motora: ninguém aprende a
                    dirigir estudando o manual do carro. E é por isso que{" "}
                    <strong className="text-foreground">treinamento equipe comercial</strong> que
                    depende só de agenda de reunião mensal quase nunca move indicador.
                  </p>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    Simulação com IA: onde o vendedor novo pode errar sem custo
                  </h2>
                  <p>
                    O maior limitador do onboarding é o tempo do gestor. Simular exige alguém do outro
                    lado interpretando cliente, ouvindo, provocando objeção e devolvendo um feedback
                    honesto. Nenhum líder comercial consegue fazer isso vinte vezes por semana com
                    cada novato, e é aí que o treinamento morre — não por falta de vontade, mas por
                    falta de disponibilidade.
                  </p>
                  <p>
                    É exatamente esse ponto que o <strong className="text-foreground">Vendas Trainer</strong>{" "}
                    resolve. O vendedor novo treina contra personas de IA que reagem como o mercado
                    dele, recebe nota e feedback logo depois de cada conversa e vê no relatório onde
                    perdeu o cliente: abriu falando de si, não investigou, apresentou preço cedo,
                    esqueceu de pedir o próximo passo. O gestor acompanha a evolução sem precisar
                    estar presente em cada tentativa, e usa o tempo dele onde importa — no
                    acompanhamento de negócio real.
                  </p>
                  <div className="my-8">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Treinar meus vendedores novos com IA
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    O que fazer com o vendedor experiente que chega novo na empresa
                  </h2>
                  <p>
                    Existe uma armadilha silenciosa aqui. Quando o contratado tem dez anos de estrada,
                    o onboarding vira uma formalidade: apresenta o time, entrega o acesso e solta.
                    Mas experiência é bagagem, não tradução automática. Ele traz um repertório que
                    funcionava para outro ticket, outro ciclo, outro tipo de comprador. Se ninguém
                    ajustar, ele vende do jeito antigo dentro do seu mercado — e demora meses para
                    entender por que a taxa de conversão dele caiu.
                  </p>
                  <p>
                    Um caso que ilustra bem: um vendedor vindo de varejo, acostumado a fechar no mesmo
                    dia, entrou num negócio B2B de ciclo longo com comitê de compras. Ele pressionava
                    fechamento na primeira reunião, o comprador recuava, e ele interpretava como
                    desinteresse. Faltavam duas coisas simples — entender que naquele contexto o
                    objetivo da primeira conversa era mapear decisores, e treinar a pergunta que
                    descobre quem mais participa da decisão. Duas semanas de simulação resolveram o
                    que seis meses de tentativa e erro não tinham resolvido.
                  </p>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    Feedback que muda comportamento é específico e imediato
                  </h2>
                  <p>
                    "Precisa ser mais consultivo" não é feedback, é opinião. O vendedor novo ouve,
                    concorda e não sabe o que fazer diferente amanhã. Feedback que muda comportamento
                    aponta o momento exato: "no minuto três você perguntou se ele tinha interesse; ali
                    a pergunta certa era como ele resolve isso hoje e o que incomoda no fornecedor
                    atual". Quanto mais próximo da conversa, maior a chance de virar hábito.
                  </p>
                  <p>
                    E há um efeito colateral positivo quando isso se torna rotina: o time deixa de ver
                    correção como julgamento. Numa equipe em que todos são gravados, avaliados e
                    ajustados semanalmente, receber apontamento é normal. Nas equipes em que só o
                    novato é observado, o feedback vira sinal de ameaça, e o vendedor passa a esconder
                    as ligações ruins — justamente as que ensinariam mais.
                  </p>

                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-6">
                    Onboarding não termina: ele muda de assunto
                  </h2>
                  <p>
                    O onboarding não tem linha de chegada, tem mudança de pauta. No começo, o assunto é
                    condução de conversa e descoberta. Depois, negociação, defesa de valor e
                    fechamento. Mais adiante, gestão do próprio pipeline e previsibilidade. Empresas
                    que encerram o treinamento quando o vendedor bate a primeira meta acabam com um
                    time inteiro parado no mesmo nível — competente no início da conversa e frágil na
                    hora de sustentar preço.
                  </p>
                  <p>
                    Se você está montando isso agora e quer entender também a estrutura em volta
                    (quem contratar, como remunerar, qual processo sustentar), vale ler{" "}
                    <Link to="/posts/como-montar-equipe-de-vendas-do-zero" className="text-primary underline">
                      como montar uma equipe de vendas do zero
                    </Link>{" "}
                    e o guia de{" "}
                    <Link to="/posts/treinamento-de-vendas" className="text-primary underline">
                      treinamento de vendas
                    </Link>
                    . Aqui o recado é mais direto: pare de entregar material e comece a fazer o
                    vendedor novo falar. Quem treina conversa, encurta ramp-up. Quem treina slide,
                    treina apresentador.
                  </p>

                  <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                    <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                      Seu próximo vendedor novo pode começar treinado
                    </h3>
                    <p className="mb-5">
                      Crie sua conta no Vendas Trainer, escolha as personas do seu mercado e deixe o
                      time simular conversas reais com feedback e nota a cada tentativa.
                    </p>
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ComoTreinarVendedoresNovos;
